import { HTTP } from '../../services/baseHttpSvc';
import _ from 'lodash';
import { convertAnyWho5AssessmentsToPositiveBaseScore } from '@/utilities/convertWho5Scores.js';

require('promise.prototype.finally').shim();

const initialFilters = {
  // the coaching status of user
  coachingStatus: null,
  // the id of selected coach
  coachId: null,
  // assessment severities
  severities: [],
  // filter by improving or declining trends
  assessmentTrend: null,
  // list of wellness programs
  focuses: [],
  // Number of days within which an interaction has occurred
  lastInteractionDays: null,
  // the patient's email
  patientEmail: null
};

const state = {
  // list of all coaches
  coaches: [],
  // filters for coaching participants
  coachingFilters: Object.assign({}, initialFilters),
  // indicator if coaching call is in-progress
  coachingLoading: false,
  // list of coaching participants
  coachingPatients: {
    patients: []
  },
  // message thread history for patient
  coachingMessages: {},
  // Recent coaching messages displayed in snapshot
  recentCoachingMessages: [],
  // Pagination settings for coaching dashboard list
  coachingDashboardSettings: {
    pageSize: 20,
    page: 1
  },
  // Canned response messages
  cannedResponseMessages: [],
  // Coaching message attachment internal link options
  coachingMessageInternalLinkOptions: [],
  // Promise keeping track of any in-progress coach dashboard searches
  coachingSearchPromise: null
};

const getters = {
  coaches: state => state.coaches,
  coachById: state => coachId => {
    if (!_.isEmpty(state.coaches)) {
      let match = _.find(state.coaches, { id: coachId });
      if (match) {
        return match.name;
      }
    }
    return '';
  },
  coachingFilters: state => state.coachingFilters,
  hasCoachingFilters: state => {
    if (
      !_.isEmpty(state.coachingFilters) &&
      (!_.isEmpty(state.coachingFilters.coachingStatus) ||
        state.coachingFilters.coachId ||
        state.coachingFilters.severities.length > 0 ||
        state.coachingFilters.assessmentTrend ||
        state.coachingFilters.focuses.length > 0 ||
        state.coachingFilters.lastInteractionDays ||
        state.coachingFilters.patientEmail)
    ) {
      return true;
    }
    return false;
  },
  coachingLoading: state => state.coachingLoading,
  coachingPatients: state => state.coachingPatients,
  coachingMessages: state => state.coachingMessages,
  recentCoachingMessages: state => state.recentCoachingMessages,
  coachingDashboardSettings: state => state.coachingDashboardSettings,
  cannedResponseMessages: state => state.cannedResponseMessages,
  coachingMessageInternalLinkOptions: state =>
    state.coachingMessageInternalLinkOptions,
  usersCoachId(state, getters, rootState, rootGetters) {
    const user = rootGetters.user;
    const isCoachSupervisor = rootGetters.isCoachSupervisor;
    if (isCoachSupervisor) {
      return null;
    }
    if (!user.id || !getters.coaches || getters.coaches.length < 1) {
      return undefined;
    }
    return getters.coaches.filter(item => item.userId === user.id)[0]['id'];
  }
};

const actions = {
  getCoaches({ commit }) {
    return HTTP.get('/coaching/coaches').then(response => {
      commit('setCoaches', response.data);
    });
  },
  getCoachingPatients(context) {
    const payload = {
      ...getFilterParams(
        context.getters.coachingFilters,
        context.getters.coachingDashboardSettings
      )
    };

    // If truthy always override coachId with usersCoachId
    if (context.getters.usersCoachId) {
      payload.coachId = context.getters.usersCoachId;
    }

    context.commit('setCoachingLoading', true);
    const requestPromise = HTTP.post('/patients/search/coaching', payload);
    context.commit('setCoachingSearchPromise', requestPromise);
    return requestPromise
      .then(response => {
        // only update state with response if the request promise matches the latest dashboard search request
        if (requestPromise === context.state.coachingSearchPromise) {
          for (let patient of response.data.patients) {
            convertAnyWho5AssessmentsToPositiveBaseScore(
              patient.assessmentScores
            );
          }
          context.commit('setCoachingPatients', response.data);
        }
      })
      .finally(() => context.commit('setCoachingLoading', false));
  },
  getCoachingMessages(context, id) {
    return HTTP.get(`/coaching/messages/users/${id}`).then(response => {
      context.commit('setCoachingMessages', response.data);
    });
  },
  getRecentCoachingMessages(context, id) {
    return HTTP.get(`/coaching/users/${id}/messages?limit=5`).then(response => {
      context.commit('setRecentCoachingMessages', response.data);
    });
  },
  getCannedResponseMessages(context) {
    return HTTP.get('editor/coaching/canned-response').then(response => {
      context.commit('setCannedResponseMessages', response.data);
    });
  },
  getCoachingMessageInternalLinkOptions(context) {
    return HTTP.get(
      'editor/coaching/canned-response/internal-link-options'
    ).then(response => {
      context.commit('setCoachingMessageInternalLinkOptions', response.data);
    });
  },
  sendCoachingMessage(context, messageData) {
    return HTTP.post('/coaching/messages', messageData);
  },
  markCoachingMessageAsRead(context, messageId) {
    return HTTP.post(`/coaching/messages/read/${messageId}`);
  },
  setUserCrisisStatus(context, crisisPayload) {
    const crisisStatus = crisisPayload.enableCrisis ? 'MANUAL' : 'NONE';
    return HTTP.patch(
      `/patients/${crisisPayload.userId}?crisisStatus=${crisisStatus}`
    );
  },
  sendCoachingEvent(context, eventData) {
    return HTTP.post('coaching/events', eventData);
  },
  saveCannedResponseMessages(context, cannedMessageData) {
    return HTTP.post('editor/coaching/canned-response', cannedMessageData);
  },
  deleteCannedResponseMessage(context, cannedMessageId) {
    return HTTP.delete(`editor/coaching/canned-response/${cannedMessageId}`);
  }
};

const mutations = {
  setCoaches(state, coaches) {
    state.coaches = coaches;
  },
  setCoachingFilters(state, { filters, isCoachSupervisor, usersCoachId }) {
    let newFilters = {};

    if (filters && Object.keys(filters).length > 0) {
      // merge filters with coachingFilters. filters wins if there are any collisions.
      newFilters = { ...state.coachingFilters, ...filters };
    } else {
      // reset to initial state
      newFilters = initialFilters;
    }

    if (!isCoachSupervisor && usersCoachId) {
      newFilters.coachId = usersCoachId;
    }

    state.coachingFilters = newFilters;
  },
  setCoachingLoading(state, loading) {
    state.coachingLoading = loading;
  },
  setCoachingPatients(state, patientArray) {
    state.coachingPatients = patientArray;
  },
  setCoachingMessages(state, messages) {
    state.coachingMessages = messages;
  },
  setRecentCoachingMessages(state, messages) {
    state.recentCoachingMessages = messages;
  },
  setCoachingDashboardSettings(state, settings) {
    // merge settings. settings wins if any collisions
    state.coachingDashboardSettings = {
      ...state.coachingDashboardSettings,
      ...settings
    };
  },
  setCoachingSearchPromise(state, promise) {
    state.coachingSearchPromise = promise;
  },
  setCannedResponseMessages(state, cannedResponseMessages) {
    // Convert tags string to array
    cannedResponseMessages = cannedResponseMessages.map(message => {
      if (message.tags && message.tags.length > 0) {
        message.tags = message.tags.split(',').map(item => item.trim());
      }

      return message;
    });

    state.cannedResponseMessages = cannedResponseMessages;
  },
  setCoachingMessageInternalLinkOptions(state, internalLinkOptions) {
    state.coachingMessageInternalLinkOptions = internalLinkOptions;
  }
};

function getFilterParams(filters, pageSettings) {
  if (!filters) {
    return {
      rowCount: pageSettings.pageSize,
      page: pageSettings.page
    };
  }

  var params = {
    coachId: filters.coachId,
    userStatus: filters.coachingStatus,
    currentFocus: filters.focuses,
    patientEmail: filters.patientEmail,
    assessmentSeverity: _.clone(filters.severities),
    rowCount: pageSettings.pageSize,
    page: pageSettings.page
  };

  if (
    !_.isEmpty(params.assessmentSeverity) &&
    _.findIndex(params.assessmentSeverity, function(s) {
      return s === 'SEVERE';
    }) > -1
  ) {
    // filtering by severe should also retrieve Extremely Severe
    params.assessmentSeverity.push('EXTREMELYSEVERE');
  }

  if (!Number.isNaN(filters.lastInteractionDays)) {
    params.lastInteractionDays = filters.lastInteractionDays;
  }
  if (filters.assessmentTrend) {
    params.assessmentTrendingStatus = filters.assessmentTrend.toUpperCase();
  }
  return params;
}

export default {
  state,
  getters,
  actions,
  mutations
};
